import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add,
  Chat,
  Delete,
  AccessTime,
  QuestionAnswer
} from '@mui/icons-material';
import { getAllSessions, deleteSession, syncSessionsFromBackend } from '../utils/sessionStorage';

const SessionListPage = ({ onSelectSession, onNewSession }) => {
  const [sessions, setSessions] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    try {
      // Sync from backend first
      const allSessions = await syncSessionsFromBackend();
      setSessions(allSessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
      // Fallback to localStorage
      setSessions(getAllSessions());
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (session, e) => {
    e.stopPropagation();
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (sessionToDelete) {
      await deleteSession(sessionToDelete.id);
      loadSessions();
      setDeleteDialogOpen(false);
      setSessionToDelete(null);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            AI Planning Sessions
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your saved analysis conversations and insights
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={onNewSession}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': {
              backgroundColor: 'primary.dark'
            }
          }}
        >
          New Session
        </Button>
      </Box>

      {/* Session Grid */}
      {sessions.length === 0 ? (
        <Card sx={{ textAlign: 'center', py: 8 }}>
          <QuestionAnswer sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No saved sessions yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Start a new AI analysis session to begin
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={onNewSession}
          >
            Start Your First Session
          </Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {sessions.map((session) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={session.id}>
              <Card
                sx={{
                  height: 220,
                  maxWidth: 320,
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => onSelectSession(session)}
              >
                <CardContent sx={{ flexGrow: 1, p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                    <Chat sx={{ color: 'primary.main', mr: 1, mt: 0.5, fontSize: 20 }} />
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        flexGrow: 1, 
                        fontWeight: 600, 
                        fontSize: '0.95rem',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word'
                      }}
                    >
                      {session.title}
                    </Typography>
                  </Box>
                  
                  {session.preview && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 1.5,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        fontSize: '0.8rem',
                        lineHeight: 1.4,
                        wordBreak: 'break-word'
                      }}
                    >
                      {session.preview.substring(0, 100)}...
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip
                      icon={<QuestionAnswer sx={{ fontSize: 14 }} />}
                      label={`${session.messageCount}`}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      icon={<AccessTime sx={{ fontSize: 14 }} />}
                      label={formatDate(session.updatedAt)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    {session.corpusName && (
                      <Chip
                        label={session.corpusName}
                        size="small"
                        variant="filled"
                        sx={{ 
                          height: 20, 
                          fontSize: '0.65rem',
                          backgroundColor: 'rgba(71, 189, 198, 0.15)',
                          color: 'primary.dark'
                        }}
                      />
                    )}
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 1.5, py: 1, borderTop: 1, borderColor: 'divider' }}>
                  <Button
                    size="small"
                    onClick={() => onSelectSession(session)}
                    sx={{ fontSize: '0.75rem' }}
                  >
                    Open
                  </Button>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteClick(session, e)}
                    sx={{ color: 'error.main' }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Session?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{sessionToDelete?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SessionListPage;

